import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {isLoaded, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {getAllRiderCodes, groupRiderCodes1, groupRiderCodes2} from 'ApplicationEntry/models/ProductsRiders/Shop/Shop';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  SalaryDeductionOccupationClassModel,
  UnassignedSalaryDeductionOccupationClassModel,
} from '../SalaryDeductionOccupationClassModel';
import * as RI from '../RiderInformation';

type ShopProps = {
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  riderCodeList: I.List<string>;
  riderAmounts: I.List<number>;
  packageOption: StringUnion;
  situs: StringUnion;
  replacement: boolean;
  policyNumber: PolicyNumber;
  riderCoverage: boolean;
  riderPremium: boolean;
  jstamp: boolean;
  packageList: PackageModel;
  plan: StringUnion;
  riderInformation: RI.default;
  occClass: SalaryDeductionOccupationClassModel;
};

export class InvalidGroupShop {
  public readonly key = 'InvalidGroupShop';

  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCoverage: boolean,
    public readonly riderPremium: boolean,
    public readonly jstamp: boolean,
    public readonly packageList: PackageModel,
    public readonly plan: StringUnion,
    public readonly riderInformation: RI.default,
    public readonly occClass: SalaryDeductionOccupationClassModel
  ) {}
}

export class ValidGroupShop {
  public static createApp({
    planType,
    premium,
    calculatedPremium,
    cwa,
    section125,
    riderCodeList,
    riderAmounts,
    packageOption,
    situs,
    replacement,
    policyNumber,
    riderCoverage,
    riderPremium,
    jstamp,
    packageList,
    plan,
    riderInformation,
    occClass,
  }: ShopProps) {
    return this.create(
      planType,
      premium,
      calculatedPremium,
      cwa,
      section125,
      riderCodeList,
      riderAmounts,
      packageOption,
      situs,
      replacement,
      policyNumber,
      riderCoverage,
      riderPremium,
      jstamp,
      packageList,
      plan,
      riderInformation,
      occClass
    );
  }

  public static create(
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    riderCodeList: I.List<string>,
    riderAmounts: I.List<number>,
    packageOption: StringUnion,
    situs: StringUnion,
    replacement: boolean,
    policyNumber: PolicyNumber,
    riderCoverage: boolean,
    riderPremium: boolean,
    jstamp: boolean,
    packageList: PackageModel,
    plan: StringUnion,
    riderInformation: RI.default,
    occClass: SalaryDeductionOccupationClassModel
  ) {
    return isNonEmptyString(planType) && isLoaded(packageList)
      ? new ValidGroupShop(
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          riderCodeList,
          riderAmounts,
          packageOption,
          situs,
          replacement,
          policyNumber,
          riderCoverage,
          riderPremium,
          jstamp,
          packageList,
          plan,
          riderInformation,
          occClass
        )
      : new InvalidGroupShop(
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          riderCodeList,
          riderAmounts,
          packageOption,
          situs,
          replacement,
          policyNumber,
          riderCoverage,
          riderPremium,
          jstamp,
          packageList,
          plan,
          riderInformation,
          occClass
        );
  }

  public readonly key = 'ValidGroupShop';
  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCoverage: boolean,
    public readonly riderPremium: boolean,
    public readonly jstamp: boolean,
    public readonly packageList: Loaded<object[]>,
    public readonly plan: StringUnion,
    public readonly riderInformation: RI.default,
    public readonly occClass: SalaryDeductionOccupationClassModel
  ) {}
}

export type GroupShop = InvalidGroupShop | ValidGroupShop;

export type GroupShopOptions<TReturn> = ToPartialMatchOptions<GroupShop, 'key', TReturn>;

export const matchGroupShop = <TReturn>(options: GroupShopOptions<TReturn>) => (app: GroupShop): TReturn => {
  switch (app.key) {
    case 'InvalidGroupShop':
      return options.InvalidGroupShop ? options.InvalidGroupShop(app) : options.default(app);
    case 'ValidGroupShop':
      return options.ValidGroupShop ? options.ValidGroupShop(app) : options.default(app);
  }
};

export const EmptyGroupShop = new InvalidGroupShop(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  I.List<string>(),
  I.List<number>(),
  new EmptyString(),
  new EmptyString(),
  false,
  EmptyPolicyNumber,
  false,
  false,
  false,
  new UnassignedPackageModel(),
  new EmptyString(),
  new RI.default(I.List<string>(), I.List<number>(), groupRiderCodes2),
  new UnassignedSalaryDeductionOccupationClassModel()
);

export const changePlanType = (planType: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, planType: NonEmptyString.create(planType)});
export const changePremium = (premium: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSection = (section: boolean) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, section125: section});
export const changePackage = (packageOption: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, packageOption: NonEmptyString.create(packageOption)});
export const changeSitus = (situs: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, situs: NonEmptyString.create(situs)});
export const changeReplacement = (replacement: boolean) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, replacement});
export const changePolicyNumber = (policyNum: string) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(policyNum))});
export const changeRiderAmount = (riderAmt: number, index: number) => (app: GroupShop) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);
  return ValidGroupShop.createApp({...app, riderAmounts: newList});
};
export const changeRiderCoverage = (riderCoverage: boolean) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, riderCoverage});
export const changeRiderPremium = (riderPremium: boolean) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, riderPremium});
export const changeOccClass = (occClass: SalaryDeductionOccupationClassModel) => (app: GroupShop) =>
  ValidGroupShop.createApp({...app, occClass});

export const changePackageItem = (packageItem: object) => (app: GroupShop) => {
  let riderCodes: RI.GroupRiderCode[] = [];
  if (app.plan.value === 'GVSP2') {
    riderCodes = groupRiderCodes2;
  } else {
    riderCodes = groupRiderCodes1;
  }

  let riderAmounts: I.List<number> = app.riderInformation.amounts;
  let riderCodeList: I.List<string> = app.riderInformation.codes;

  riderCodes.forEach((riderCode, index) => {
    let riderCodePropertyName;

    if (packageItem.hasOwnProperty((riderCodePropertyName = (riderCode as RI.GroupRiderCode).propertyName))) {
      const amount =
        packageItem[riderCodePropertyName] === null ? Number.NaN : parseFloat(packageItem[riderCodePropertyName]);

      const riderInformation = RI.default.changeRiderInformation(
        amount,
        riderCode.code,
        index,
        riderAmounts,
        riderCodeList
      );

      riderCodeList = riderInformation.riderCodes;
      riderAmounts = riderInformation.riderAmounts;
    }
  });

  if (app.plan.value === 'GVSP2') {
    return ValidGroupShop.createApp({
      ...app,
      riderInformation: new RI.default(riderCodeList, riderAmounts, riderCodes),
    });
  } else {
    return ValidGroupShop.createApp({
      ...app,
      riderCoverage: packageItem['strikeLayoff'] === '1' ? true : false,
      riderPremium: packageItem['returnOfPremium'] === '1' ? true : false,
      riderInformation: new RI.default(riderCodeList, riderAmounts, riderCodes),
    });
  }
};

export const changeRiderCodes = (plan: string) => (app: GroupShop) => {
  let existingList: I.List<string> = app.riderCodeList;
  const plans = getAllRiderCodes(plan);
  plans.forEach(rider => {
    existingList = existingList.push(rider.code);
  });
  return ValidGroupShop.createApp({...app, riderCodeList: existingList});
};
export const changeJStamp = (jstamp: boolean) => (app: GroupShop) => ValidGroupShop.createApp({...app, jstamp});
export const changePackageList = (packages: PackageModel) => (app: GroupShop) => {
  return ValidGroupShop.createApp({...app, packageList: packages});
};

export const changePlan = (plan: string) => (app: GroupShop) => {
  if (plan === 'GVSP2') {
    return ValidGroupShop.createApp({
      ...app,
      plan: NonEmptyString.create(plan),
      packageOption: new EmptyString(),
      premium: new EmptyString(),
      cwa: new EmptyString(),
      section125: false,
      riderCodeList: I.List<string>(),
      riderAmounts: I.List<number>(),
      situs: new EmptyString(),
      replacement: false,
      policyNumber: EmptyPolicyNumber,
      riderCoverage: false,
      riderPremium: false,
      jstamp: false,
      packageList: new UnassignedPackageModel(),
      planType: new EmptyString(),
      riderInformation: new RI.default(I.List<string>(), I.List<number>(), groupRiderCodes2),
    });
  } else {
    return ValidGroupShop.createApp({
      ...app,
      plan: NonEmptyString.create(plan),
      packageOption: new EmptyString(),
      premium: new EmptyString(),
      cwa: new EmptyString(),
      section125: false,
      riderCodeList: I.List<string>(),
      riderAmounts: I.List<number>(),
      situs: new EmptyString(),
      replacement: false,
      policyNumber: EmptyPolicyNumber,
      riderCoverage: false,
      riderPremium: false,
      jstamp: false,
      packageList: new UnassignedPackageModel(),
      planType: new EmptyString(),
      riderInformation: new RI.default(I.List<string>(), I.List<number>(), groupRiderCodes1),
    });
  }
};

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: GroupShop) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  if (app.plan.value === 'GVSP2') {
    return ValidGroupShop.createApp({
      ...app,
      riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, groupRiderCodes2),
    });
  } else {
    return ValidGroupShop.createApp({
      ...app,
      riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, groupRiderCodes1),
    });
  }
};
